/**
 * Shared medical / Medicare validation for Parent Information.
 *
 * The wizard gates navigation on the same rules the form renders inline, so a
 * disabled "Next" button can always name the field that is still missing.
 */

import { cardSideMissing } from './storedCardImages';

export const MEDICARE_NUMBER_REGEX = /^\d{10}[-#]\d$/;
export const MEDICARE_BASE_REGEX = /^\d{10}$/;
export const MEDICARE_REF_REGEX = /^\d$/;

/**
 * TASS may send `2727446626#3` (hash) or `2727446626-3` (hyphen).
 * The 10 digits are the card number; the digit after # or - is the IRN/ref.
 */
export const splitMedicareNumber = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw || raw.toUpperCase() === 'NA') {
        return { base: '', ref: '', separator: '#' };
    }

    const match = raw.match(/^(\d{1,10})\s*[-#]\s*(\d)?$/);
    if (match) {
        return {
            base: match[1],
            ref: match[2] || '',
            separator: raw.includes('#') ? '#' : '-',
        };
    }

    const digits = raw.replace(/\D/g, '');
    return {
        base: digits.slice(0, 10),
        ref: digits.length > 10 ? digits.slice(10, 11) : '',
        separator: '#',
    };
};

export const getMedicareBase = (value) => splitMedicareNumber(value).base;
export const getMedicareRef = (value) => splitMedicareNumber(value).ref;

/** TASS stores expiry as MM/YYYY, MM-YYYY or YYYY-MM. Returns the 1st of that month. */
export const parseMonthYear = (value) => {
    if (value == null) return null;

    const raw = String(value).trim();
    if (!raw || raw.toUpperCase() === 'NA') return null;

    const parts = raw.replace(/-/g, '/').split('/');
    if (parts.length !== 2) return null;

    const [first, second] = parts;
    const monthPart = first.length === 4 ? second : first;
    const yearPart = first.length === 4 ? first : second;

    if (yearPart.length !== 4) return null;

    const month = Number(monthPart);
    const year = Number(yearPart);

    if (!Number.isInteger(month) || !Number.isInteger(year)) return null;
    if (month < 1 || month > 12) return null;
    if (year < 2000 || year > 2100) return null;

    return new Date(year, month - 1, 1);
};

export const isValidMonthYear = (value) => parseMonthYear(value) != null;

/** A card is only usable when it expires in a month later than the current one. */
export const isMonthYearInFuture = (value, now = new Date()) => {
    const parsed = parseMonthYear(value);
    if (!parsed) return false;
    return parsed > new Date(now.getFullYear(), now.getMonth(), 1);
};

export const isStudentEnrolled = (student) =>
    student != null && student.enrollStatus !== 'Not Enrolled';

const isMissing = (value) => value === null || value === undefined || value === '';

/**
 * Everything still outstanding for one student, as `{ field, message }`.
 * Students who are not being re-enrolled never block the flow.
 */
export const getStudentMedicalErrors = (student, options = {}) => {
    const {
        parentNoMedicare = false,
        parentMedicareNumber = '',
        parentMedicareExpiry = '',
        studentMedicareImages = [],
        studentStoredCard = [],
    } = options;

    if (!isStudentEnrolled(student)) return [];

    const errors = [];
    const isTempVisa = Boolean(student.istemporaryVisa || student.temporary_visa);
    const usesOwnCard = Boolean(student.hasCustomMedicare) || parentNoMedicare;

    if (!isTempVisa) {
        if (usesOwnCard) {
            const ownCard = splitMedicareNumber(student.medicare_number);
            if (!MEDICARE_BASE_REGEX.test(ownCard.base) || !MEDICARE_REF_REGEX.test(ownCard.ref)) {
                errors.push({
                    field: 'medicare_number',
                    message: 'Enter a valid Medicare number (10 digits) and reference number.',
                });
            }

            const expiry = student.medicare_expiry;
            if (isMissing(expiry) || expiry === 'NA') {
                errors.push({
                    field: 'medicare_expiry',
                    message: 'Medicare expiry date is required.',
                });
            } else if (!isValidMonthYear(expiry)) {
                errors.push({
                    field: 'medicare_expiry',
                    message: 'Enter the Medicare expiry as MM/YYYY.',
                });
            } else if (!isMonthYearInFuture(expiry)) {
                errors.push({
                    field: 'medicare_expiry',
                    message: 'Medicare expiry must be later than the current month.',
                });
            }

            // A card already uploaded counts. Otherwise a parent returning to the screen is told
            // to upload one they have already sent.
            if (cardSideMissing(
                Array.isArray(studentMedicareImages) ? studentMedicareImages : [],
                studentStoredCard
            )) {
                errors.push({
                    field: 'medicare_images',
                    message: 'Upload both the front and back of this student’s Medicare card.',
                });
            }
        } else {
            if (!MEDICARE_BASE_REGEX.test(String(parentMedicareNumber || ''))) {
                errors.push({
                    field: 'family_medicare_number',
                    message: 'Add the 10-digit family Medicare number, or tick "Student has a different Medicare card".',
                });
            }

            if (!MEDICARE_REF_REGEX.test(String(student.medicare_ref_no ?? ''))) {
                errors.push({
                    field: 'medicare_ref_no',
                    message: 'Enter the Medicare reference number shown next to this student on the card.',
                });
            }

            if (!isValidMonthYear(parentMedicareExpiry)) {
                errors.push({
                    field: 'family_medicare_expiry',
                    message: 'Add a valid family Medicare expiry (MM/YYYY).',
                });
            } else if (!isMonthYearInFuture(parentMedicareExpiry)) {
                errors.push({
                    field: 'family_medicare_expiry',
                    message: 'The family Medicare expiry must be later than the current month.',
                });
            }
        }
    }

    if (isMissing(student.ambulance_cover)) {
        errors.push({ field: 'ambulance_cover', message: 'Select an Ambulance Cover option.' });
    }

    if (isMissing(student.private_health_insurance)) {
        errors.push({ field: 'private_health_insurance', message: 'Select a Private Health Insurance option.' });
    }

    if (isMissing(student.immunisation_status)) {
        errors.push({ field: 'immunisation_status', message: 'Select an Immunisation Status option.' });
    }

    if (isMissing(student.allergy)) {
        errors.push({ field: 'allergy', message: 'Select an Allergy option.' });
    }

    return errors;
};

export const isStudentMedicalComplete = (student, options) =>
    getStudentMedicalErrors(student, options).length === 0;

/**
 * Family card problems that must be fixed before the medical screens.
 */
export const getFamilyMedicareErrors = ({ noMedicare = false, number = '', expiry = '' } = {}) => {
    if (noMedicare) return [];

    const errors = [];

    if (!MEDICARE_BASE_REGEX.test(String(number || ''))) {
        errors.push('Enter the 10-digit family Medicare number, or tick "I don\'t have one".');
    }

    if (isMissing(expiry)) {
        errors.push('Enter the family Medicare expiry date (MM/YYYY).');
    } else if (!isValidMonthYear(expiry)) {
        errors.push('Enter the family Medicare expiry as MM/YYYY.');
    } else if (!isMonthYearInFuture(expiry)) {
        errors.push('The family Medicare card is expired. Enter an expiry later than the current month.');
    }

    return errors;
};
